import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useOrgId } from '@/app/session';
import { Banner, Button, Drawer, ErrorState, LinkButton, Skeleton, Time } from '@/components/ui';
import { PartyView, type PartyDrawerState } from '@/features/parties/PartyDrawers';
import { locationQuery, type Location } from './api';
import { useStageMove } from './commands';
import { KeyFacts } from './KeyFacts';
import { ConversationsSection, FilesSection } from './LocationSections';
import { PeopleSection } from './PeopleSection';
import { fullAddress, ownerOf, SiteHero, SiteMenu, SiteNotices, StageControl } from './SiteParts';
import { CompanyPanels } from './CompanyPanels';
import { ResearchSection } from '@/features/research/ResearchSection';
import { LocationDescription } from './LocationDescription';
import { HistoricalCorrespondenceSection } from '@/features/historical-correspondence/HistoricalCorrespondenceSection';
import { HistoricalNotesSection } from '@/features/historical-notes/HistoricalNotesSection';
import './locations.css';

/**
 * The site opened over the board or a conversation (`?site=`): satellite header, stage, key facts, people,
 * conversations and files. A person or company opened from here replaces the content, with a
 * way back (`&contact=` / `&company=`), so the browser back button walks the same path.
 */
export function SitePanel({
  locationId,
  party,
  onParty,
  onClose,
}: {
  locationId: string;
  party: PartyDrawerState;
  onParty: (next: PartyDrawerState) => void;
  onClose: () => void;
}) {
  const orgId = useOrgId();
  const q = useQuery(locationQuery(orgId, locationId));
  const inParty = Boolean(party.contact || party.company);
  const sheet = useRef<HTMLDivElement>(null);
  // Swapping the site and a person or company unmounts what was clicked; keep focus in the panel.
  useEffect(() => {
    if (document.activeElement === document.body) sheet.current?.focus({ preventScroll: true });
  }, [party.contact, party.company]);
  return (
    <Drawer open onClose={onClose} title={<span className="visually-hidden">{q.data?.name ?? 'Site'}</span>}>
      <div ref={sheet} className="sheet site-sheet" tabIndex={-1}>
        {inParty ? (
          <div className="site-sheet-party">
            <Button variant="quiet" size="sm" icon="chevronLeft" className="site-sheet-back" onClick={() => onParty({})}>
              {q.data?.name ?? 'Back'}
            </Button>
            <PartyView party={party} onOpen={onParty} sitesInPlace />
          </div>
        ) : q.isPending ? (
          <PanelSkeleton />
        ) : q.isError ? (
          <div className="site-sheet-pad">
            <ErrorState error={q.error} onRetry={() => void q.refetch()} />
          </div>
        ) : (
          <SitePanelBody location={q.data} onParty={onParty} onDeleted={onClose} />
        )}
      </div>
    </Drawer>
  );
}

function PanelSkeleton() {
  return (
    <div aria-busy="true" aria-label="Loading site">
      <div className="site-hero site-hero-skeleton" style={{ height: 220 }} />
      <div className="site-sheet-pad stack">
        <Skeleton width="60%" />
        <Skeleton width="100%" height={56} />
        <Skeleton width="80%" />
      </div>
    </div>
  );
}

function SitePanelBody({ location: l, onParty, onDeleted }: { location: Location; onParty: (next: PartyDrawerState) => void; onDeleted: () => void }) {
  const move = useStageMove();
  const owner = ownerOf(l);
  const address = fullAddress(l);
  return (
    <>
      <SiteHero location={l} height={220} as="p" headingProps={{ 'aria-hidden': true }} />
      <div className="site-sheet-pad site-sheet-body">
        <div className="site-lede">
          <p className="site-address">{address || 'No address'}</p>
          {owner ? (
            <p className="site-owner">
              Owner{' '}
              <button type="button" className="site-inline-link" onClick={() => onParty({ company: owner.company_id })}>
                {owner.company_name}
              </button>
            </p>
          ) : null}
          <SiteNotices location={l} />
        </div>
        {l.deleted_at ? (
          <Banner title="Deleted">
            <Time value={l.deleted_at} /> · Read only
          </Banner>
        ) : null}
        <div className="site-stage-row">
          <StageControl location={l} move={move} />
          <div className="site-stage-actions">
            <LinkButton to="/app/board/$locationId" params={{ locationId: l.id }} variant="secondary" size="sm">
              Open full page
            </LinkButton>
            <SiteMenu location={l} move={move} onDeleted={onDeleted} />
          </div>
        </div>
        <KeyFacts location={l} columns={1} />
        <div className="site-sections">
          <LocationDescription description={l.description} />
          <ResearchSection kind="location" targetId={l.id} />
          <CompanyPanels location={l} />
          <PeopleSection location={l} onOpenContact={(id) => onParty({ contact: id })} />
          <HistoricalCorrespondenceSection locationId={l.id} />
          <HistoricalNotesSection locationId={l.id} />
          <ConversationsSection location={l} />
          <FilesSection location={l} />
        </div>
      </div>
      {move.dialog}
    </>
  );
}
